import React, { useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { noop } from 'ui-components/default-props/noop';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { PrintModalContent } from './print-modal-content';
import { documentTypeToRealProps } from './print-modal-content/constants';
import style from './print-modal.module.less';
import { usePackingSlipTemplate } from './use-packing-slip-template';
import { emptyArray } from 'ui-components/default-props/empty';
import { DocumentType } from '../../../enums/document-type';
import VoidAction from '../../../orders/modals/order-details-modal/order-details-modal-content/shipments/section-expander-and-actions/actions/void';

export const PrintModal = ({
  printer,
  documentType,
  modalFrameworkMethods,
  documentToPrint = '',
  fulfillmentPlanIds = emptyArray,
  fulfillmentIds = emptyArray,
  fulfillmentPlanSetIds = emptyArray,
  fulfillments = emptyArray,
  rememberPrintSettings = false,
  setRememberPrintSettings,
  setPrintDestination,
  onPrintClick,
  allocateFulfillmentPlans = noop,
  isTestLabel = false,
}) => {
  const [documentUrl, setDocumentUrl] = useState('');
  const [internalPrinter, setInternalPrinter] = useState(printer);
  const [downloadAndPrintDisabled, setDownloadAndPrintDisabled] = useState(true);
  const { closeModal } = modalFrameworkMethods;
  const showVoidButton = !!fulfillments.length;

  const { formatMessage } = useIntl();

  const documentCount =
    documentType === DocumentType.Label.identifier || documentType === DocumentType.BatchLabel.identifier
      ? fulfillmentIds.length
      : fulfillmentPlanIds.length;

  const updateDocumentUrl = useCallback(
    (newDocumentUrl) => {
      const { getFileNameTranslation } = documentTypeToRealProps[documentType];
      const isAbsoluteUrl = newDocumentUrl.startsWith('blob:') || newDocumentUrl.startsWith('http');

      if (getFileNameTranslation && !isAbsoluteUrl) {
        const fileName = getFileNameTranslation(documentType);
        const translatedFileName = formatMessage(fileName, {
          count: documentCount,
        });
        const urlWithFileName = new URL(newDocumentUrl, window.location.origin);
        urlWithFileName.searchParams.append('displayName', encodeURIComponent(translatedFileName));
        setDocumentUrl(`${urlWithFileName.pathname}${urlWithFileName.search}`);
      } else {
        setDocumentUrl(newDocumentUrl);
      }
    },
    [documentType, formatMessage, documentCount],
  );

  useEffect(() => {
    updateDocumentUrl(documentToPrint);
  }, [documentToPrint, updateDocumentUrl]);

  const { templates, selectedTemplate, changeTemplate } = usePackingSlipTemplate(
    fulfillmentPlanIds,
    setDownloadAndPrintDisabled,
    updateDocumentUrl,
  );

  const onPrint = useCallback(() => {
    const print = () => {
      setPrintDestination(internalPrinter);
      const templateId =
        documentType === DocumentType.PackingSlip.identifier || documentType === DocumentType.BatchPackingSlip.identifier
          ? selectedTemplate.packingSlipId
          : 0;
      onPrintClick(
        internalPrinter,
        documentType,
        fulfillmentPlanIds,
        fulfillmentIds,
        templateId,
        documentUrl,
        isTestLabel,
        fulfillmentPlanSetIds,
      );
    };

    if (documentType === DocumentType.PackingSlip.identifier) {
      allocateFulfillmentPlans(fulfillmentPlanIds, print);
    } else {
      print();
    }

    closeModal();
  }, [
    allocateFulfillmentPlans,
    closeModal,
    documentUrl,
    documentType,
    fulfillmentIds,
    fulfillmentPlanIds,
    internalPrinter,
    isTestLabel,
    onPrintClick,
    selectedTemplate.packingSlipId,
    setPrintDestination,
    fulfillmentPlanSetIds,
  ]);

  const handleFileLoaded = useCallback(() => setDownloadAndPrintDisabled(false), [setDownloadAndPrintDisabled]);

  return (
    <Modal className={style['print-modal']}>
      <ModalBody className={style['print-modal-body']}>
        <PrintModalContent
          changeTemplate={changeTemplate}
          closeModal={closeModal}
          documentCount={documentCount}
          documentType={documentType}
          documentUrl={documentUrl}
          downloadAndPrintDisabled={downloadAndPrintDisabled}
          fulfillmentIds={fulfillmentIds}
          fulfillmentPlanIds={fulfillmentPlanIds}
          fulfillmentPlanSetIds={fulfillmentPlanSetIds}
          onFileLoaded={handleFileLoaded}
          printDestination={internalPrinter}
          rememberPrintSettings={rememberPrintSettings}
          selectedTemplate={selectedTemplate}
          setPrintDestination={setInternalPrinter}
          setRememberPrintSettings={setRememberPrintSettings}
          templates={templates}
        />
      </ModalBody>
      <ModalFooter
        className={classNames(style['print-modal-footer'], {
          [style['void-container']]: showVoidButton,
        })}
      >
        {showVoidButton ? (
          <VoidAction
            buttonText={
              <FormattedMessage
                defaultMessage='Void {numberOfFulfillments, plural, one {Label} other {Labels}}'
                description='Void button text from inside print modal'
                id='R8DXXu'
                values={{ numberOfFulfillments: fulfillments.length }}
              />
            }
            buttonTheme='tertiary'
            fulfillments={fulfillments}
            selectedFulfillmentIds={fulfillmentIds}
          />
        ) : null}
        <div>
          <Button onClick={closeModal} theme='tertiary'>
            <FormattedMessage defaultMessage='Cancel' description='printModal.cancel' id='B287LT' />
          </Button>
          <Button disabled={!printer || downloadAndPrintDisabled} onClick={onPrint} theme='primary'>
            <FormattedMessage defaultMessage='Print through ShipStation Connect' description='printModal.printButton' id='9O+oDo' />
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

PrintModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  onPrintClick: PropTypes.func.isRequired,
  documentType: PropTypes.string.isRequired,
  printer: PropTypes.object,
  rememberPrintSettings: PropTypes.bool,
  setRememberPrintSettings: PropTypes.func.isRequired,
  documentToPrint: PropTypes.string,
  fulfillmentPlanIds: PropTypes.array,
  fulfillmentIds: PropTypes.array,
  fulfillmentPlanSetIds: PropTypes.array,
  fulfillments: PropTypes.array,
  isTestLabel: PropTypes.bool,
  allocateFulfillmentPlans: PropTypes.func,
  setPrintDestination: PropTypes.func.isRequired,
};
